/* eslint-disable react-hooks/exhaustive-deps */
import { chatCooldownUntilAtomFamily } from '@/atoms/chatCooldownAtoms';
import { chatAbortControllersAtom, chatCapacityStatusAtomFamily, chatStateAtom, pendingFormTemplateIdAtom, streamingChatIdsAtom, useCreditsWhenNeededAtom } from '@/atoms/chatAtoms';
import {
  computeCooldownUntil,
  formatCooldownClock,
  isCooldownActive,
} from '@/components/chat/utils/chatCooldown';
import { resolveAttachableLinkedContext } from '@/components/chat/utils/resolveAttachableLinkedContext';
import { sidebarChatIdAtomFamily } from '@/atoms/chatSidebarAtoms';
import { evidenceCapabilitiesAtom } from '@/atoms/evidenceCapabilitiesAtoms';
import { evidenceSourceFiltersAtom, includeWebSearchAtom, isSourceControlReadyForUser, SOURCE_CONTROL_DEFAULTS, SOURCE_CONTROL_SYNC_LIFECYCLE_RESOLVING, sourceControlReadinessAtom, sourceControlSyncLifecycleAtom, sourceControlToggleAtom, sourceControlWriterMountedAtom, type SourceControlReadiness } from '@/atoms/sourceControlAtoms';
import { getSourceControlFailOpenTimeoutMs, reportSourceControlFailOpen } from '@/hooks/useSourceControlReadinessFailOpen';
import {
  getActiveCollectionIds,
  getHasActiveLibrary,
  normalizePersistedSourceControlState,
  partitionCollectionIds,
  resolveLibraryCollectionPruneIds,
} from '@/atoms/sourceControlSelectors';
import { activeSlideContextAtom, presentationAtom } from '@/atoms/slideBuilderAtoms';
import type { EvidenceSourceFilters } from '@heidi/evidence/contracts';
import type {
  CapacityChunk,
  ChatMode,
  ChatMetadataChunkUnion,
  Chunk,
  ErrorItemEntry,
  PptxExportChunk,
  PrintDocumentChunk,
  ReasoningItemEntry,
  SearchMetadataChunk,
  SlideUpdateChunk,
  TimelineEntry,
} from '@/components/chat/types/types';
import { textContainsQuotaExhaustedBillingLink } from '@/components/chat/constants/quotaPaywall';
import { fireEvidenceQuotaConsumedToast } from '@/components/chat/components/fireEvidenceQuotaConsumedToast';
import { finalizeTimeline } from '@/components/chat/utils/finalizeTimeline';
import { presentationPreviewAtom } from '@/components/slide-builder/presentationPreviewAtom';
import { sanitizeSourceConfig } from '@/components/chat/utils/sanitizeSourceConfig';
import {
  IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED,
  IS_UNIFIED_COLLECTIONS_ENABLED,
} from '@/features/flags/legacy-flags';
import { useIsEvidenceQueryLimitsForLegacyTiersEnabled, useIsLinkedContextProvenanceGateKilled, useIsMidChatClinicalLinkPersistEnabled, useIsProjectsEnabled } from '@/features/flags';
import {
  appendCheckpoint,
  finishTracking,
  getActiveTrackings,
  TrackingKey,
} from '@heidi/runtime/performance';
import { ADVANCED_EVIDENCE_IN_SESSION_QUOTA } from '@/hooks/useEvidenceInSessionLimits';
import { useCollections } from '@heidi/evidence/data';
import { useSourceLists, useUnmigratedSourceLists } from '@heidi/evidence/data';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import useReverseTrial from '@/hooks/useReverseTrial';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useTeamEvidenceEnforcement } from '@/hooks/useTeamEvidenceEnforcement';
import { handlePptxExportChunk, handleSlideDeleteChunk, handleSlideUpdateChunk } from '@/components/slide-builder/hooks/useSlideUpdateListener';
import { getCountryRaw } from '@heidi/utils/get-country';
import type { ChatMessageSourcePayload } from '@/models/analytics/features/chat';
import type { EvidenceChatErrorType } from '@/models/analytics/features/evidence';
import { useMutateUpdateUser } from '@/hooks/mutation/useMutateUpdateUser';
import { GET_CHAT_BY_ID } from '@/hooks/query/useQueryChatById';
import { GET_CHATS } from '@/hooks/query/useQueryChats';
import { GET_DOCUMENTS_BY_ID } from '@heidi/session/contracts';
import { GET_SESSION_CHATS } from '@heidi/session/data';
import { GET_USER } from '@heidi/workspace/contracts';
import { useQueryUser } from '@heidi/workspace/data';
import type { components } from '@heidi/schema/ml-scribe';

type UserResponseShape = components['schemas']['UserResponse'];
import type { ChatListResponse } from '@heidi/session/contracts';
import type { ChatResponse } from '@/types';
import { logger } from '@/utils/logger/logger';
import {
  classifyStreamError,
  createTimelineStream,
  type EvidenceStreamErrorCode,
  type IntegrationPartnerRequest,
  type NetworkTimingCaptureResult,
  type SlideContextPayload,
  type StreamCloseInfo,
  type StreamErrorContext,
  type StreamLifecycle,
  TIMELINE_STREAM_ERROR,
} from '@/utils/timelineEventSource';
import { IS_EVIDENCE_OFFLINE_FAIL_FAST_ENABLED } from '@/features/flags/legacy-flags';
import { shouldEvidenceFailFastWhenOffline } from '@/components/chat/utils/evidenceOfflineGuard';
import { abortStreamWithReason, STREAM_ABORT_REASON } from '@/utils/streamAbortReason';
import { addDatadogRumError } from '@/utils/tracing';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { getDefaultStore, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { buildMessageHistory } from '../utils/chatHelpers';
import { generateId } from '@/utils/generateId';
import { handlePrintDocumentChunk } from '../utils/handlePrintDocumentChunk';
import { processChatChunk } from '../utils/processChatChunk';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { useIntl } from 'react-intl';

const getSourceControlUserId = (
  user: UserResponseShape | undefined
): string | null => user?.user_ref_id ?? user?.kinde_user_id ?? null;

/**
 * How the readiness wait resolved. `fail-open` sends must not trust the
 * cached toggle atom (it is not user-scoped and survives same-tab account
 * switches) — the payload assembly substitutes `SOURCE_CONTROL_DEFAULTS`.
 */
type SourceControlReadinessMode = 'hydrated' | 'fail-open';

const readinessModeFor = (
  readiness: SourceControlReadiness
): SourceControlReadinessMode =>
  readiness.status === 'ready' && readiness.source === 'fail-open'
    ? 'fail-open'
    : 'hydrated';

const waitForSourceControlReadiness = (
  store: ReturnType<typeof getDefaultStore>,
  getCurrentUserId: () => string | null
): Promise<SourceControlReadinessMode> => {
  const readiness = store.get(sourceControlReadinessAtom);
  if (isSourceControlReadyForUser(readiness, getCurrentUserId())) {
    return Promise.resolve(readinessModeFor(readiness));
  }
  if (readiness.status === 'blocked') {
    return Promise.reject(new Error('Source control settings are unavailable'));
  }

  return new Promise<SourceControlReadinessMode>((resolve, reject) => {
    let isSettled = false;
    const cleanups: Array<() => void> = [];
    const addCleanup = (cleanup: () => void) => {
      if (isSettled) {
        cleanup();
      } else {
        cleanups.push(cleanup);
      }
    };
    const finish = (
      error?: Error,
      mode: SourceControlReadinessMode = 'hydrated'
    ) => {
      if (isSettled) return;
      isSettled = true;
      cleanups.forEach((cleanup) => cleanup());
      if (error) {
        reject(error);
      } else {
        resolve(mode);
      }
    };
    const settle = () => {
      const next = store.get(sourceControlReadinessAtom);
      if (isSourceControlReadyForUser(next, getCurrentUserId())) {
        finish(undefined, readinessModeFor(next));
      } else if (next.status === 'blocked') {
        finish(new Error('Source control settings are unavailable'));
      }
    };

    const unsubscribe = store.sub(sourceControlReadinessAtom, settle);
    addCleanup(unsubscribe);
    // Clamped remote-tunable deadline, resolved per wait (see
    // getSourceControlFailOpenTimeoutMs).
    const timeoutMs = getSourceControlFailOpenTimeoutMs();
    const timeoutId = setTimeout(() => {
      // INC-349 / DEV-12867 fail-open: a hydration stall must never block the
      // send. Proceed — with default selections, see the assembly below — and
      // report it. The `useSourceControlReadinessFailOpen` watchdog normally
      // flips the atom first; this is the local net if that write never lands.
      reportSourceControlFailOpen(
        {
          writerMounted: store.get(sourceControlWriterMountedAtom),
          lifecyclePublished:
            store.get(sourceControlSyncLifecycleAtom) !==
            SOURCE_CONTROL_SYNC_LIFECYCLE_RESOLVING,
        },
        'send-path',
        timeoutMs
      );
      finish(undefined, 'fail-open');
    }, timeoutMs);
    addCleanup(() => clearTimeout(timeoutId));
    settle();
  });
};

interface StreamCompleteInfo {
  durationMs: number;
  timeToFirstTokenMs?: number;
  referencesCount: number;
  responseLength: number;
  tavilySearchCount?: number;
  /** EVI-731: The mode the backend routed to (from mode_info chunk) */
  routedMode?: string;
  /** Tool names used in this response (e.g., 'evidence_search', 'medical_calculator') */
  toolsUsed?: string[];
  /** Response length in words */
  responseWordCount?: number;
  /** Backend message_id from message_info chunk */
  messageId?: string;
  /** Chat ID from chat_metadata chunk (reliable even across remounts) */
  chatId?: string;
  /** True when a quota-exhausted paywall block was rendered in the response. */
  quotaExhaustedMessageShown?: boolean;
}

/** Get only the response entries from the latest message (after basePosition) */
const getLatestResponseEntries = (
  timeline: TimelineEntry[],
  basePosition: number | undefined
): TimelineEntry[] => {
  if (basePosition === undefined) return timeline;
  return timeline.slice(basePosition);
};

/** Helper to count references from web search items in timeline */
const countReferencesInTimeline = (timeline: TimelineEntry[]): number => {
  return timeline.reduce((count, entry) => {
    if (entry.type === 'web_search_item') {
      return count + (entry.sources?.length || 0);
    }
    return count;
  }, 0);
};

/** Whether the latest response rendered a quota-exhausted paywall message. */
const hasQuotaExhaustedMessageInTimeline = (timeline: TimelineEntry[]): boolean =>
  timeline.some(
    (entry) =>
      entry.type === 'quota_exhausted_item' ||
      (entry.type === 'text_item' &&
        (!!entry.quotaExceeded ||
          textContainsQuotaExhaustedBillingLink(entry.content)))
  );

/** Helper to calculate total response length from text items in timeline */
const calculateResponseLength = (timeline: TimelineEntry[]): number => {
  return timeline.reduce((length, entry) => {
    if (entry.type === 'text_item' && entry.content) {
      return length + entry.content.length;
    }
    return length;
  }, 0);
};

/** Tool types that map from timeline entry types to analytics tool names */
const TOOL_TYPE_MAP: Record<string, string> = {
  web_search_item: 'evidence_search',
  library_search_item: 'library_search',
  patient_summary_item: 'get_patient_summary',
  pdf_export_item: 'export_to_pdf',
  print_document_item: 'print_document',
  patient_evidence_pdf_item: 'patient_evidence_pdf',
  email_patient_evidence_item: 'email_patient_evidence',
  calculator_item: 'medical_calculator',
  formula_calculator_item: 'formula_calculator',
  clinical_task_result_item: 'clinical_task',
  sessions_by_date_item: 'get_sessions_by_date',
  appointments_item: 'get_appointments',
  search_templates_item: 'search_templates',
  search_community_templates_item: 'search_community_templates',
  search_patient_profiles_item: 'search_patient_profiles',
  create_template_item: 'create_template',
  edit_template_section_item: 'edit_template_section',
  csv_export_item: 'export_to_csv',
  word_export_item: 'generate_word',
  excel_export_item: 'export_to_excel',
  form_fill_item: 'fill_pdf_form',
};

/** Helper to count words in text items in timeline */
const calculateWordCount = (timeline: TimelineEntry[]): number => {
  return timeline.reduce((count, entry) => {
    if (entry.type === 'text_item' && typeof entry.content === 'string') {
      return count + entry.content.trim().split(/\s+/).filter(Boolean).length;
    }
    return count;
  }, 0);
};

/**
 * Whether an entry constitutes a user-visible answer: streamed text, a tool
 * deliverable (download card, calculator result, sources), a paywall block,
 * or an error message. Reasoning/thinking activity does not count — a turn
 * that rendered only activity indicators still left the user without a reply.
 */
const isVisibleAnswerEntry = (entry: TimelineEntry): boolean =>
  entry.type === 'text_item' ||
  entry.type === 'error_item' ||
  entry.type === 'quota_exhausted_item' ||
  entry.type in TOOL_TYPE_MAP;

/** Extract unique tool names used in a set of timeline entries */
const extractToolsUsed = (entries: TimelineEntry[]): string[] => {
  const tools = new Set<string>();
  for (const entry of entries) {
    const toolName = TOOL_TYPE_MAP[entry.type];
    if (toolName) {
      tools.add(toolName);
    }
  }
  return Array.from(tools);
};

// Singleton-only dedup helper: it inspects the by-key store, so it takes the
// singleton checkpoint shape rather than appendCheckpoint's full key|handle union.
const appendCheckpointIfMissing = (params: {
  key: TrackingKey;
  checkpointName: string;
  metadata?: Record<string, unknown>;
}) => {
  const activeTracking = getActiveTrackings().get(params.key);
  if (
    activeTracking?.checkpoints.some(
      (checkpoint) => checkpoint.name === params.checkpointName
    )
  ) {
    return;
  }

  appendCheckpoint(params);
};

interface StreamErrorInfo {
  errorMessage: string;
  durationMs?: number;
  /** EVI-731: The mode the backend routed to (from mode_info chunk) */
  routedMode?: string;
  errorType?: 'stream_mid' | 'stream_start';
  /** HTTP status of the failed request, when one reached us. */
  httpStatus?: number;
  /** PHI-safe semantic failure code (never derived from body text). */
  errorCode?: EvidenceStreamErrorCode;
  /** Whether any content chunk arrived before the failure. */
  contentStarted?: boolean;
}

/**
 * Flattens the SSE lifecycle snapshot into the snake_case shape RUM expects on
 * the `perf.evidence.message_send` action, so the clean-close and gap terminal
 * reports carry an identical set of lifecycle fields.
 */
const lifecycleToMetadata = (lifecycle: StreamLifecycle) => ({
  close_reason: lifecycle.closeReason,
  chunks_received: lifecycle.chunksReceived,
  ...(lifecycle.lastChunkType !== undefined && {
    last_chunk_type: lifecycle.lastChunkType,
  }),
  saw_stream_complete: lifecycle.sawStreamComplete,
  supports_terminal_sentinel: lifecycle.supportsTerminalSentinel,
  truncated: lifecycle.truncated,
  ms_since_last_event: lifecycle.msSinceLastEvent,
  document_hidden: lifecycle.documentHidden,
  ...(lifecycle.abortReason !== undefined && { abort_reason: lifecycle.abortReason }),
});

const EVIDENCE_STREAM_RUM_ERROR_NAME = 'EvidenceChatStreamError';

/**
 * Raises a Datadog RUM *error* for a genuine evidence-stream failure.
 *
 * The `perf.evidence.message_send` action already records the failure, but a
 * custom action is not trace-correlated, so it can't pivot to the backend trace
 * (Datadog greys out "View Traces" on actions). A RUM error raised in the same
 * view as the trace-linked SSE send resource restores that pivot and lands the
 * failure in Error Tracking. A stable name + `error_type`-keyed message gives
 * Error Tracking one clean issue per cause rather than splintering on the
 * variable raw messages.
 *
 * Telemetry footprint — the context attaches exactly these bounded fields, and
 * never response text or the query:
 *   - `source` — fixed `"evidence_chat_stream"`
 *   - `error_type` — bounded `EvidenceChatErrorType` enum
 *   - `content_started` — boolean
 *   - `chat_id` / `message_id` — session-level identifiers (may be null);
 *     correlatable to backend records but carry no clinical content themselves
 *   - `routed_mode` / `http_status` / `error_code` — attached only when known
 * Benign terminals (`client_abort`, `recovery_started`, success) are deliberately
 * NOT reported — they are not failures and would pollute Error Tracking and the
 * error rate.
 */
const reportEvidenceStreamRumError = (params: {
  errorType: EvidenceChatErrorType;
  chatId: string | null;
  messageId: string | null;
  contentStarted: boolean;
  routedMode?: string;
  httpStatus?: number;
  errorCode?: EvidenceStreamErrorCode;
}) => {
  const error = new Error(`${EVIDENCE_STREAM_RUM_ERROR_NAME}: ${params.errorType}`);
  error.name = EVIDENCE_STREAM_RUM_ERROR_NAME;
  addDatadogRumError(error, {
    source: 'evidence_chat_stream',
    error_type: params.errorType,
    chat_id: params.chatId,
    message_id: params.messageId,
    content_started: params.contentStarted,
    ...(params.routedMode !== undefined && { routed_mode: params.routedMode }),
    ...(params.httpStatus !== undefined && { http_status: params.httpStatus }),
    ...(params.errorCode !== undefined && { error_code: params.errorCode }),
  });
};

interface UseChatStreamParams {
  stateKey: string;
  analyticsSessionId: string;
  sessionId: string | null | undefined;
  chatId: string | null | undefined;
  mode?: ChatMode;
  version?: number; // Evidence mode version: 1 (GPT-5 + LangChain) or 2 (Anthropic native)
  content?: string;
  domains?: string[];
  includeUserDocs?: boolean; // Whether to search user-uploaded documents
  includeWebSearch?: boolean; // Whether to include web search results (false = library-only mode)
  evidenceSourceFilters?: EvidenceSourceFilters; // Array of enabled evidence source filters (EVI-493)
  sourceListIds?: string[]; // IDs of user's web collections (source lists) for filtered search
  userDocCollectionIds?: string[]; // IDs of user's document collections to filter library search
  collectionIds?: string[]; // Unified collection IDs — resolves both documents AND domains
  integrationPartners?: IntegrationPartnerRequest[]; // EVI-678: Integration partner configurations
  isInSession?: boolean; // Whether this is an in-session Ask Heidi request (uses ask_ai quota)
  activeTab?: string; // Active tab type: "NOTE" or "DOCUMENT"
  activeDocumentId?: string; // Document ID when on a DOCUMENT tab
  onStreamStart?: () => void;
  onStreamComplete?: () => void;
  onEvidenceStreamComplete?: (info: StreamCompleteInfo) => void;
  onEvidenceStreamError?: (info: StreamErrorInfo) => void;
  /** Called when the backend creates a new chat (chat_id received in stream metadata) */
  onChatCreated?: (chatId: string) => void;
  /** Called to attempt recovery polling instead of showing an error immediately */
  onStartRecovery?: (
    chatId: string,
    options: { onFallback: () => void; maxPollDurationMs?: number }
  ) => void;
}

export const useChatStream = ({
  stateKey,
  analyticsSessionId,
  sessionId,
  chatId,
  mode,
  version,
  content,
  domains,
  includeUserDocs,
  includeWebSearch,
  evidenceSourceFilters,
  sourceListIds,
  userDocCollectionIds,
  collectionIds,
  integrationPartners,
  isInSession,
  activeTab,
  activeDocumentId,
  onStreamStart,
  onStreamComplete,
  onEvidenceStreamComplete,
  onEvidenceStreamError,
  onChatCreated,
  onStartRecovery,
}: UseChatStreamParams) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const setChatState = useSetAtom(chatStateAtom);
  const setStreamingChatIds = useSetAtom(streamingChatIdsAtom);
  const { t } = useTranslations();
  const intl = useIntl();
  const { data: userData } = useQueryUser();
  const userDataRef = useRef(userData);
  userDataRef.current = userData;
  // Mirror of the in-session evidence quota launch flag for the chunk handler.
  // Hooks can't be called inside `processChunk`, and the flag value can flip
  // mid-session (PostHog refresh), so we keep a live ref for the gate.
  // Heidi II accounts are billed through the agent credit allowance, not these credits.
  const isHeidiIIEnabled = useIsProjectsEnabled();
  const isEvidenceQuotaToastFlagEnabled =
    useIsEvidenceQueryLimitsForLegacyTiersEnabled() && !isHeidiIIEnabled;
  const isEvidenceQuotaToastFlagRef = useRef(isEvidenceQuotaToastFlagEnabled);
  isEvidenceQuotaToastFlagRef.current = isEvidenceQuotaToastFlagEnabled;
  const isMidChatClinicalLinkPersistEnabled = useIsMidChatClinicalLinkPersistEnabled();
  const isMidChatClinicalLinkPersistRef = useRef(isMidChatClinicalLinkPersistEnabled);
  isMidChatClinicalLinkPersistRef.current = isMidChatClinicalLinkPersistEnabled;
  // Same ref-mirror pattern: the send closure is long-lived and the kill
  // switch must take effect on the next send after a remote flip.
  const isProvenanceGateKilled = useIsLinkedContextProvenanceGateKilled();
  const isProvenanceGateKilledRef = useRef(isProvenanceGateKilled);
  isProvenanceGateKilledRef.current = isProvenanceGateKilled;
  // Resolve the user's upgrade target here so the non-React toast helper can
  // pick the correct tier colours. Inlined alongside the popover + toast-button
  // copies (vs a shared hook) to keep this PR purely additive — extract into
  // `useEvidenceQuotaUpgradeModal` when a 4th consumer appears. Same ref-pattern
  // as the flag above: the target can flip mid-session if billing tier changes.
  const { data: newUserTier } = useQueryUserBillingInfo((user) => user?.new_product_tier);
  const { isClinicianPracticeTrialUser } = useReverseTrial();
  const isPracticeUpgradeTarget =
    newUserTier === SubscriptionTier.PRO_PLUS || isClinicianPracticeTrialUser;
  const isPracticeUpgradeTargetRef = useRef(isPracticeUpgradeTarget);
  isPracticeUpgradeTargetRef.current = isPracticeUpgradeTarget;
  const { mutate: updateUser } = useMutateUpdateUser({ enableInvalidation: false });
  const { data: collectionsData, isLoading: isCollectionsLoading } = useCollections();
  const { data: availableSourceLists } = useSourceLists();
  const {
    data: unmigratedSourceLists,
    isLoading: isUnmigratedSourceListsLoading,
  } = useUnmigratedSourceLists();

  const enforcement = useTeamEvidenceEnforcement();
  const enforcementRef = useRef(enforcement);
  enforcementRef.current = enforcement;
  // In-session evidence = on session page (path does not include '/chat'), not full-screen chat
  const isInSessionEvidence = !!sessionId && !router.pathname.includes('/chat');
  // OPTIMIZATION: Use useSetAtom + store.get() instead of useAtom to prevent
  // re-renders when abort controller changes. We only need to read the value
  // in callbacks, not subscribe to changes.
  const setAbortController = useSetAtom(chatAbortControllersAtom(stateKey));
  const storeRef = useRef(getDefaultStore());
  const store = storeRef.current;
  const stateKeyRef = useRef(stateKey);
  stateKeyRef.current = stateKey;
  const streamingModeRef = useRef<ChatMode | undefined>(undefined);
  // EVI-731: Separate ref to capture the backend's routed mode (from mode_info chunk)
  // without overwriting streamingModeRef, which controls timeline entry formatting.
  const routedModeRef = useRef<string | undefined>(undefined);
  const activeStateKeyRef = useRef<string>(stateKey);
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  // Keep ref in sync with prop changes
  activeStateKeyRef.current = stateKey;

  // Handle metadata chunks (e.g., chat title updates, active protocol badge, search metadata)
  const handleMetadata = useCallback(
    (metadata: ChatMetadataChunkUnion | SearchMetadataChunk) => {
      // Handle search metadata for analytics
      if (metadata.type === 'search_metadata') {
        tavilySearchCountRef.current = metadata.tavily_search_count;
        return;
      }

      // Handle chat metadata (title updates, chat_id)
      if (metadata.type === 'chat_metadata' && metadata.chat_id) {
        const currentKey = activeStateKeyRef.current;
        const isFirstChatInStream = !chatId;
        if (isFirstChatInStream) {
          // Notify that a new chat was created by the backend (for analytics)
          onChatCreatedRef.current?.(metadata.chat_id);
          appendCheckpointIfMissing({
            key: TrackingKey.EVIDENCE_MESSAGE_SEND,
            checkpointName: 'chat_created',
            metadata: {
              chat_id: metadata.chat_id,
            },
          });
          appendCheckpointIfMissing({
            key: TrackingKey.EVIDENCE_IN_SESSION_SUGGESTION_CLICK,
            checkpointName: 'chat_created',
            metadata: {
              chat_id: metadata.chat_id,
            },
          });
        }
        if (
          isFirstChatInStream &&
          isInSessionEvidence &&
          IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() &&
          !userData?.has_used_evidence_in_session
        ) {
          updateUser({ has_used_evidence_in_session: true });
        }
        // Save chat_id to conversation state for analytics tracking
        setChatState((prev) => {
          const existingState = prev[currentKey];
          if (!existingState) return prev;
          // Only update if clientChatId is not already set
          if (existingState.metadata.clientChatId) return prev;
          return {
            ...prev,
            [currentKey]: {
              ...existingState,
              metadata: {
                ...existingState.metadata,
                clientChatId: metadata.chat_id,
              },
            },
          };
        });
      }
      if (metadata.type === 'chat_metadata' && metadata.title && metadata.chat_id) {
        const updateChatTitle = (chats: ChatListResponse['chats']) =>
          chats.map((chat) =>
            chat.id === metadata.chat_id ? { ...chat, title: metadata.title } : chat
          );

        // Update paginated chat list caches (any page/size) with new title
        queryClient.setQueriesData<ChatListResponse>({ queryKey: [GET_CHATS] }, (old) => {
          if (!old?.chats) return old;
          return { ...old, chats: updateChatTitle(old.chats) };
        });

        // Update infinite query caches (used by ChatSidebar) with new title
        queryClient.setQueriesData<InfiniteData<ChatListResponse, number>>(
          { queryKey: [GET_CHATS, 'infinite'] },
          (old) => {
            if (!old?.pages) return old;
            return {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                chats: updateChatTitle(page.chats),
              })),
            };
          }
        );

        // Update individual chat cache so useChatBackend consumers get the new title
        queryClient.setQueryData<ChatResponse>(
          [GET_CHAT_BY_ID, metadata.chat_id, true],
          (old) => old ? { ...old, title: metadata.title } : old
        );

        // When a chat is auto-created during session-based streaming (e.g.,
        // protocol execution), we must:
        //   1. Migrate chat state from the session key to the chat key
        //   2. Update activeStateKeyRef so subsequent chunks go to the new key
        //   3. Set the sidebar chatId atom (which triggers ChatRenderer remount)
        //
        // The migration MUST happen before the sidebar atom update because the
        // key change remounts ChatRenderer (different React key), and the
        // useLayoutEffect migration in useChat.ts won't fire on a fresh mount.
        // Without this, "New chat" would reuse the session key and show stale
        // content, and navigating back to the chat would show a skeleton.
        const currentSessionId = sessionIdRef.current;
        if (currentSessionId) {
          const sessionKey = `session-${currentSessionId}-new`;
          const chatKey = `chat-${metadata.chat_id}`;

          // Step 1: Migrate state from session key → chat key
          setChatState((prev) => {
            const oldState = prev[sessionKey];
            const newState = prev[chatKey];

            const oldHasData =
              oldState && (oldState.timeline.length > 0 || oldState.streamingState);
            const newIsEmpty =
              !newState || (newState.timeline.length === 0 && !newState.streamingState);

            if (oldHasData && newIsEmpty) {
              const result = { ...prev, [chatKey]: oldState };
              delete result[sessionKey];
              return result;
            }
            return prev;
          });

          // Step 2: Update ref so subsequent SSE chunks write to the chat key
          activeStateKeyRef.current = chatKey;

          // Step 3: Set sidebar chatId → triggers ChatRenderer remount with new chatId
          store.set(sidebarChatIdAtomFamily(currentSessionId), metadata.chat_id);

          // Invalidate session chats query so the new chat appears in the list
          void queryClient.invalidateQueries({
            queryKey: [GET_SESSION_CHATS, currentSessionId],
          });

          logger.info('[chat_metadata] Migrated state and updated sidebar chatId', {
            sessionId: currentSessionId,
            chatId: metadata.chat_id,
            fromKey: sessionKey,
            toKey: chatKey,
          });
        }
      }
    },
    [
      chatId,
      isInSessionEvidence,
      queryClient,
      store,
      setChatState,
      updateUser,
      userData?.has_used_evidence_in_session,
    ]
  );

  const processChunk = useCallback(
    (chunk: Chunk) => {
      try {
        // Handle mode_info chunk — capture the backend's routed mode for analytics
        // EVI-731: We intentionally do NOT update streamingModeRef here. The frontend
        // always sends AUTO or EVIDENCE, and timeline entries should keep that mode so
        // formatting (key points, citations, references) always renders correctly.
        // The backend's routed mode is stored separately in routedModeRef for PH tracking.
        if (chunk.type === 'mode_info') {
          routedModeRef.current = chunk.mode; // e.g. 'legacy' or 'evidence'
          // mode_info doesn't add to timeline, just captures routed mode
          return;
        }

        // Ambient mid-stream quota update. Merge the fresh in-session usage
        // into the cached UserResponse so the quota badge / popover re-renders
        // immediately instead of waiting for the post-stream /user invalidate,
        // then fire the post-consume System Toast (Figma 10492:57298) when
        // the consume was an advanced one — the BE only emits the chunk for
        // non-excluded tiers, but we still gate by the FE flag to keep the
        // FE/BE rollout invariant if PostHog hasn't fully propagated yet.
        // No timeline impact — return before the streaming-state bookkeeping.
        if (chunk.type === 'in_session_evidence_quota_update') {
          queryClient.setQueryData<UserResponseShape | undefined>(
            [GET_USER],
            (oldUser) => {
              if (!oldUser) return oldUser;
              return {
                ...oldUser,
                evidence_usage: {
                  ...oldUser.evidence_usage,
                  in_session: chunk.evidence_usage.in_session,
                },
              };
            }
          );

          const inSession = chunk.evidence_usage.in_session;
          if (
            inSession &&
            chunk.kind === 'advanced' &&
            chunk.consumed &&
            isEvidenceQuotaToastFlagRef.current
          ) {
            // GRO-3173: only fire on the consume that leaves <=1 credit so
            // the toast surfaces at most 2x/week instead of on every deduction.
            const advancedRemaining = Math.max(
              0,
              ADVANCED_EVIDENCE_IN_SESSION_QUOTA - inSession.advanced_used
            );
            if (advancedRemaining <= 1) {
              fireEvidenceQuotaConsumedToast({
                advancedUsed: inSession.advanced_used,
                currentPeriodEnd: inSession.current_period_end,
                isPracticeUpgradeTarget: isPracticeUpgradeTargetRef.current,
                currentPlan: newUserTier,
              });
            }
          }
          return;
        }

        // Capture first token time for analytics. message_info is pre-content
        // bookkeeping (index -1, arrives before any content), so it must not
        // latch — content_started / time-to-first-token would otherwise read
        // true/short on turns where nothing ever streamed.
        if (chunk.type !== 'message_info') {
          firstTokenTimeRef.current ??= Date.now();
        }

        // Fire onStreamStart callback on first chunk (stream confirmed started)
        if (!streamStartCalledRef.current) {
          streamStartCalledRef.current = true;
          appendCheckpoint({
            key: TrackingKey.EVIDENCE_MESSAGE_SEND,
            checkpointName: 'stream_started',
            metadata: {
              routed_mode: routedModeRef.current,
              chunk_type: chunk.type,
            },
          });
          appendCheckpoint({
            key: TrackingKey.EVIDENCE_IN_SESSION_SUGGESTION_CLICK,
            checkpointName: 'stream_started',
            metadata: {
              routed_mode: routedModeRef.current,
              chunk_type: chunk.type,
            },
          });
          onStreamStartRef.current?.();
        }

        if ((chunk as unknown as { type: string }).type === 'slide_update') {
          void handleSlideUpdateChunk(chunk as unknown as SlideUpdateChunk);
        }

        if ((chunk as unknown as { type: string }).type === 'pptx_export') {
          handlePptxExportChunk(chunk as unknown as PptxExportChunk);
        }

        if ((chunk as unknown as { type: string }).type === 'slide_delete') {
          handleSlideDeleteChunk(chunk as unknown as { presentation_id: string; slide_id: string; status: string });
        }

        if ((chunk as unknown as { type: string }).type === 'print_document') {
          handlePrintDocumentChunk(chunk as unknown as PrintDocumentChunk);
        }

        setChatState((prev) => {
          const currentKey = activeStateKeyRef.current;
          const session = prev[currentKey];
          if (!session) return prev;

          // Guard against chunks arriving from a previous SSE connection after
          // timeout abort + retry. Uses a ref because processChunk is defined
          // outside startStreaming and doesn't have the closure-captured id.
          if (session.streamingState?.abortControllerId !== abortControllerIdRef.current) {
            return prev;
          }

          const result = processChatChunk(chunk, session, streamingModeRef.current);
          if (!result) return prev;

          return {
            ...prev,
            [currentKey]: {
              ...session,
              timeline: result.timeline,
              streamingState: result.streamingState,
            },
          };
        });
      } catch (error) {
        logger.error('Unexpected error in chat chunk processing', {
          error,
          sessionId: analyticsSessionId,
          chunkType: chunk?.type,
        });

        // Read abort controller from store when needed (not subscribed)
        const currentAbortController = store.get(
          chatAbortControllersAtom(stateKeyRef.current)
        );
        if (currentAbortController) {
          abortStreamWithReason(
            currentAbortController,
            STREAM_ABORT_REASON.CHUNK_PROCESS_ERROR
          );
          setAbortController(null);
        }

        setChatState((prev) => {
          const currentKey = activeStateKeyRef.current;
          const session = prev[currentKey];
          if (!session) return prev;

          return {
            ...prev,
            [currentKey]: {
              ...session,
              streamingState: null,
            },
          };
        });
      }
    },
    [analyticsSessionId, setChatState, setAbortController, store]
  );

  const addErrorToTimeline = useCallback(
    (errorMessage: string) => {
      setChatState((prev) => {
        const currentKey = activeStateKeyRef.current;
        const session = prev[currentKey];
        if (!session) return prev;

        const timelineWithoutPlaceholder = session.timeline.filter(
          (entry) => !(entry.type === 'reasoning_item' && entry.backendIndex === -1)
        );

        const errorPosition = timelineWithoutPlaceholder.length;
        const errorEntry: ErrorItemEntry = {
          type: 'error_item',
          id: generateId(),
          content: errorMessage,
          position: errorPosition,
        };

        return {
          ...prev,
          [currentKey]: {
            ...session,
            timeline: [...timelineWithoutPlaceholder, errorEntry] as TimelineEntry[],
            streamingState: null,
          },
        };
      });
    },
    [setChatState]
  );

  const onStreamStartRef = useRef(onStreamStart);
  onStreamStartRef.current = onStreamStart;

  const onStreamCompleteRef = useRef(onStreamComplete);
  onStreamCompleteRef.current = onStreamComplete;

  const onEvidenceStreamCompleteRef = useRef(onEvidenceStreamComplete);
  onEvidenceStreamCompleteRef.current = onEvidenceStreamComplete;

  const onEvidenceStreamErrorRef = useRef(onEvidenceStreamError);
  onEvidenceStreamErrorRef.current = onEvidenceStreamError;


  const onChatCreatedRef = useRef(onChatCreated);
  onChatCreatedRef.current = onChatCreated;

  const onStartRecoveryRef = useRef(onStartRecovery);
  onStartRecoveryRef.current = onStartRecovery;

  // Track time to first token for analytics
  const firstTokenTimeRef = useRef<number | undefined>(undefined);
  // Track if onStreamStart has been called for this stream
  const streamStartCalledRef = useRef<boolean>(false);
  // Track Tavily search count from search_metadata chunk for analytics
  const tavilySearchCountRef = useRef<number | undefined>(undefined);
  const abortControllerIdRef = useRef<string | undefined>(undefined);

  const startStreaming = useCallback(
    async (
      timeline: TimelineEntry[],
      overrideMode?: ChatMode,
      onError?: (error: string) => void,
      activeChatId?: string | null,
      files?: File[],
      attachmentIds?: string[],
      source?: ChatMessageSourcePayload
    ) => {
      if (isCooldownActive(store.get(chatCooldownUntilAtomFamily(stateKey)))) {
        return;
      }

      let readinessMode: SourceControlReadinessMode;
      try {
        readinessMode = await waitForSourceControlReadiness(store, () => {
          return getSourceControlUserId(userDataRef.current);
        });
      } catch (error) {
        logger.error('Source control settings unavailable before stream start', {
          error,
          sessionId: analyticsSessionId,
        });
        addErrorToTimeline(TIMELINE_STREAM_ERROR.QUERY_FAILED);
        onError?.(TIMELINE_STREAM_ERROR.QUERY_FAILED);
        return;
      }
      // Access-control review (INC-349 follow-up): a fail-open send must not
      // trust the cached toggle. The jotai default store survives same-tab
      // account switches (`clearLogoutStorage` wipes localStorage, not live
      // atom values), so mid-switch the cache can still hold ANOTHER user's
      // collection IDs and partners. Substitute the user-agnostic defaults
      // (pure web search) in the payload instead. The atom itself is left
      // untouched so nothing debounce-PATCHes stale values to the BE, and the
      // writer's later hydration replaces it with the real selections.
      const isFailOpenSend = readinessMode === 'fail-open';

      // Read abort controller from store when needed (not subscribed).
      // Supersede any in-flight stream before starting the next one.
      const currentAbortController = store.get(
        chatAbortControllersAtom(stateKeyRef.current)
      );
      if (currentAbortController) {
        abortStreamWithReason(currentAbortController, STREAM_ABORT_REASON.SUPERSEDED);
        setAbortController(null);
      }

      // Reset tracking for new stream
      firstTokenTimeRef.current = undefined;
      streamStartCalledRef.current = false;
      tavilySearchCountRef.current = undefined;
      routedModeRef.current = undefined;

      const basePosition = timeline.length;
      const now = Date.now();
      const abortControllerId = generateId();
      abortControllerIdRef.current = abortControllerId;

      const modeToUse = overrideMode ?? mode;
      streamingModeRef.current = modeToUse;

      const placeholderEntry: ReasoningItemEntry = {
        type: 'reasoning_item',
        id: generateId(),
        content: [],
        position: basePosition,
        backendIndex: -1,
      };

      const timelineWithPlaceholder = [...timeline, placeholderEntry];

      const currentKey = activeStateKeyRef.current;
      setChatState((prev) => {
        // Preserve existing metadata (including clientChatId) when starting streaming
        const existingMetadata = prev[currentKey]?.metadata ?? {};
        return {
          ...prev,
          [currentKey]: {
            timeline: timelineWithPlaceholder,
            streamingState: {
              basePosition,
              activeIndices: new Map(),
              currentActiveIndex: null,
              activityGroupOpen: true,
              streamStartedAt: now,
              lastChunkAt: now,
              lastVisibleContentAt: now,
              abortControllerId,
            },
            metadata: { ...existingMetadata, lastActivity: now },
          },
        };
      });

      const messages = buildMessageHistory(timeline);

      // Use activeChatId if provided (from create endpoint), otherwise use chatId from props
      const effectiveChatId = activeChatId ?? chatId ?? null;

      // Track which chats are currently streaming at a global level so that
      // indicators can update even when the UI for a given chat is not mounted.
      if (effectiveChatId) {
        setStreamingChatIds((prev) => {
          const next = new Set(prev);
          next.add(effectiveChatId);
          return next;
        });
      }

      // Shared tail of both recovery entry points (mid-stream error and
      // content-less truncated close): tear down the streaming bookkeeping,
      // emit the paired recovery_started terminals, and hand off to polling.
      // One implementation keeps the 45s budget and the paired-terminal
      // contract in sync between the two paths. No RUM error is raised —
      // recovery is benign/self-healing and must not pollute Error Tracking.
      const beginStreamRecovery = (params: {
        recoveryChatId: string;
        terminalMetadata: Record<string, unknown>;
        fallbackMessage: string;
      }) => {
        setAbortController(null);
        if (effectiveChatId) {
          setStreamingChatIds((prev) => {
            const next = new Set(prev);
            next.delete(effectiveChatId);
            return next;
          });
        }

        finishTracking({
          key: TrackingKey.EVIDENCE_MESSAGE_SEND,
          metadata: params.terminalMetadata,
        });
        finishTracking({
          key: TrackingKey.EVIDENCE_IN_SESSION_SUGGESTION_CLICK,
          metadata: params.terminalMetadata,
        });

        onStartRecoveryRef.current?.(params.recoveryChatId, {
          onFallback: () => {
            onError?.(params.fallbackMessage);
            addErrorToTimeline(params.fallbackMessage);
          },
          maxPollDurationMs: 45_000,
        });
      };

      try {
        const newAbortController = new AbortController();
        setAbortController(newAbortController);

        // Read FRESH source config from Jotai store at stream-creation time.
        // This avoids stale closure issues when "Check other sources" updates
        // the toggle atoms and immediately triggers a new message — the atoms
        // are updated synchronously but the React re-render chain (which would
        // produce a new startStreaming closure) may not have completed yet.
        // Fail-open: defaults, never the unscoped cache (see comment above).
        const freshSourceControlToggle = isFailOpenSend
          ? SOURCE_CONTROL_DEFAULTS
          : store.get(sourceControlToggleAtom);
        const isUnified = IS_UNIFIED_COLLECTIONS_ENABLED();
        let normalizedFreshSourceControlToggle = freshSourceControlToggle;

        if (isFailOpenSend) {
          // Defaults carry no collection IDs to prune, and the write-back
          // below must not run — persisting defaults over the incoming
          // user's saved selections would be data loss.
        } else if (isUnified) {
          if (
            !isCollectionsLoading &&
            !isUnmigratedSourceListsLoading &&
            collectionsData !== undefined &&
            unmigratedSourceLists !== undefined
          ) {
            const validUnifiedIds = [
              ...collectionsData.collections.map((collection) => collection.collection_id),
              ...unmigratedSourceLists.map((sourceList) => sourceList.id),
            ];

            normalizedFreshSourceControlToggle = normalizePersistedSourceControlState(
              freshSourceControlToggle,
              {
                validLibraryCollectionIds: validUnifiedIds,
                validWebCollectionIds: validUnifiedIds,
                enforcedLibraryCollectionIds: resolveLibraryCollectionPruneIds(
                  enforcementRef.current.libraryCollectionAllowlistIds,
                  enforcementRef.current.enforcedLibraryCollectionIds
                ),
                enforcedWebCollectionIds:
                  enforcementRef.current.enforcedWebCollectionIds,
              }
            );
          }
        } else {
          // Safe to prune without a "collections loaded" guard: the allowlist
          // (enforced ∪ optional) is always a subset of valid IDs by construction.
          normalizedFreshSourceControlToggle = normalizePersistedSourceControlState(
            freshSourceControlToggle,
            {
              validLibraryCollectionIds: collectionsData?.collections.map(
                (collection) => collection.collection_id
              ),
              validWebCollectionIds: availableSourceLists?.map((list) => list.id),
              enforcedLibraryCollectionIds: resolveLibraryCollectionPruneIds(
                enforcementRef.current.libraryCollectionAllowlistIds,
                enforcementRef.current.enforcedLibraryCollectionIds
              ),
              enforcedWebCollectionIds:
                enforcementRef.current.enforcedWebCollectionIds,
            }
          );
        }

        if (
          !isFailOpenSend &&
          normalizedFreshSourceControlToggle !== freshSourceControlToggle
        ) {
          store.set(sourceControlToggleAtom, normalizedFreshSourceControlToggle);
        }

        // The derived atoms reflect the LIVE toggle atom, so under fail-open
        // read the same values off SOURCE_CONTROL_DEFAULTS instead: no source
        // filters, web search on — unless the deployment cannot search the
        // web, which the backend refuses rather than downgrades (F9, MOD-1726).
        const rawEvidenceSourceFilters = isFailOpenSend
          ? []
          : store.get(evidenceSourceFiltersAtom);
        const freshIncludeWebSearch = isFailOpenSend
          ? SOURCE_CONTROL_DEFAULTS.webSearch && store.get(evidenceCapabilitiesAtom).webSearch
          : store.get(includeWebSearchAtom);

        // Read FRESH user-doc config from the Jotai store.
        // Uses shared pure selectors so logic stays in sync with useEvidenceSourceConfig.
        const rawActiveIds = getActiveCollectionIds(
          normalizedFreshSourceControlToggle,
          isUnified
        );
        const partitioned = isUnified ? partitionCollectionIds(rawActiveIds) : null;
        const rawCollectionIds = partitioned?.collectionIds ?? [];
        const rawUserDocCollectionIds = isUnified ? [] : rawActiveIds;
        // Legacy hex IDs from allCollections (unmigrated source lists) → source_list_ids.
        // The `sourceListIds` param is render-derived from the live toggle atom,
        // so it is dropped on fail-open sends along with the rest of the cache.
        const rawSourceListIds = isFailOpenSend
          ? undefined
          : (sourceListIds ?? (
              partitioned && partitioned.legacySourceListIds.length > 0
                ? partitioned.legacySourceListIds
                : undefined
            ));
        const rawIncludeUserDocs =
          !!includeUserDocs &&
          getHasActiveLibrary(normalizedFreshSourceControlToggle, isUnified);

        // Apply FGA team enforcement to the fresh atom values.
        // useEvidenceSourceConfig sanitizes the React-render-path values, but
        // store.get() bypasses that. This ensures the request payload always
        // respects admin-configured team evidence settings.
        const {
          evidenceSourceFilters: freshEvidenceSourceFilters,
          userDocCollectionIds: freshUserDocCollectionIds,
          sourceListIds: freshSourceListIds,
          integrationPartners: freshIntegrationPartners,
          includeUserDocs: freshIncludeUserDocs,
          collectionIds: sanitizedCollectionIds,
        } = sanitizeSourceConfig(
          {
            evidenceSourceFilters: rawEvidenceSourceFilters,
            userDocCollectionIds: rawUserDocCollectionIds,
            sourceListIds: rawSourceListIds,
            // Render-derived from the live toggle atom — dropped on fail-open.
            integrationPartners: isFailOpenSend ? undefined : integrationPartners,
            includeUserDocs: rawIncludeUserDocs,
            collectionIds: isUnified ? rawCollectionIds : undefined,
          },
          enforcementRef.current
        );
        const freshCollectionIds = isUnified
          ? (sanitizedCollectionIds ?? rawCollectionIds)
          : rawCollectionIds;

        const presentationPanelOpen = store.get(presentationPreviewAtom)?.isOpen ?? false;
        const explicitCtx = store.get(activeSlideContextAtom);
        const presentation = presentationPanelOpen ? store.get(presentationAtom) : null;
        // Clear immediately after capture so the badge UI resets
        if (explicitCtx) {
          store.set(activeSlideContextAtom, null);
        }
        let slideContext: SlideContextPayload | undefined;
        if (explicitCtx && presentation) {
          slideContext = {
            presentation_id: presentation.id,
            slide_id: explicitCtx.slideId,
            slide_index: explicitCtx.slideIndex,
            title: explicitCtx.title,
            source: explicitCtx.source,
            extracted_text: explicitCtx.extractedText,
            shape_map: explicitCtx.shapeMap,
          };
        } else if (presentation && presentation.slides.length > 0) {
          slideContext = {
            presentation_id: presentation.id,
            slide_id: '',
            slide_index: -1,
            title: '',
            all_slides: presentation.slides.map((s) => ({
              slide_id: s.id,
              slide_index: s.index,
              title: s.title,
            })),
          };
        }

        // Read and clear pending form template ID (set by template picker)
        const freshFormTemplateId = store.get(pendingFormTemplateIdAtom);
        if (freshFormTemplateId) {
          store.set(pendingFormTemplateIdAtom, null);
        }

        // Read FRESH credits-when-needed preference (credits popover toggle).
        // Same rationale as the source-control atoms above: a toggle change can
        // be batched with a send, so reading from the store guarantees we use
        // the latest value rather than a stale closure capture.
        const freshUseCreditsWhenNeeded = store.get(useCreditsWhenNeededAtom);

        // Measure the Evidence chat panel at send time for responsive LLM formatting
        const chatPane = document.querySelector<HTMLElement>('[data-chat-pane="true"]');
        const measuredPanelWidth = chatPane?.clientWidth;
        const measuredPanelHeight = chatPane?.clientHeight;

        const isMidChatPersistActive = isMidChatClinicalLinkPersistRef.current;
        // Linked-context ids this send may attach — read FRESH at the send
        // boundary and provenance-gated (resolveAttachableLinkedContext).
        // Replaces both the raw atom reads and the render-closure hook params
        // this block used to fall back on: context primed from a DIFFERENT
        // chat (useChatContextLinking's backend-sync) is refused, so a stale
        // link can no longer race the new-chat cleanup and ride into this
        // chat's completions payload. The gate is remotely killable (raw-atom
        // reads restored) via `op-evidence-disable-linked-context-provenance-gate`.
        const attachableLinkedContext = resolveAttachableLinkedContext(
          store,
          effectiveChatId,
          { gateKilled: isProvenanceGateKilledRef.current }
        );

        let outgoingSessionId: string | null;
        let outgoingPatientProfileId: string | null;

        if (sessionId) {
          // EVI-2500: a page session and a patient profile are mutually
          // exclusive on the backend. A PENDING standalone link (or a
          // kill-switch raw atom read) must not ride into in-session
          // completions.
          outgoingSessionId = sessionId;
          outgoingPatientProfileId = null;
        } else if (!effectiveChatId) {
          outgoingSessionId = attachableLinkedContext.sessionId;
          outgoingPatientProfileId = attachableLinkedContext.patientProfileId;
        } else if (isMidChatPersistActive) {
          // Existing chat + flag ON: persist mid-chat links; a linked
          // patient excludes an inherited linked session.
          outgoingSessionId = attachableLinkedContext.patientProfileId
            ? null
            : attachableLinkedContext.sessionId;
          outgoingPatientProfileId = attachableLinkedContext.patientProfileId;
        } else {
          // Existing chat + flag OFF: forwarding only (create-time link paths).
          outgoingSessionId = attachableLinkedContext.sessionId;
          outgoingPatientProfileId = attachableLinkedContext.patientProfileId;
        }

        await createTimelineStream({
          messages,
          sessionId: outgoingSessionId,
          patientProfileId: outgoingPatientProfileId,
          chatId: effectiveChatId,
          mode: streamingModeRef.current,
          version,
          content,
          domains,
          files,
          attachmentIds,
          includeUserDocs: freshIncludeUserDocs,
          includeWebSearch: freshIncludeWebSearch,
          evidenceSourceFilters: freshEvidenceSourceFilters,
          sourceListIds: freshSourceListIds,
          userDocCollectionIds: freshUserDocCollectionIds,
          collectionIds: freshCollectionIds,
          integrationPartners: freshIntegrationPartners,
          useCreditsWhenNeeded: freshUseCreditsWhenNeeded,
          isInSession,
          // pass country to backend for localizing evidence search for unauthenticated users
          country: !userData ? (getCountryRaw() ?? undefined) : undefined,
          slideContext,
          formTemplateId: freshFormTemplateId ?? undefined,
          activeTab,
          activeDocumentId,
          panelWidth: measuredPanelWidth,
          panelHeight: measuredPanelHeight,
          source,
          onChunk: processChunk,
          onKeepalive: () => {
            setChatState((prev) => {
              const currentKey = activeStateKeyRef.current;
              const session = prev[currentKey];
              if (!session?.streamingState) return prev;
              if (session.streamingState.abortControllerId !== abortControllerId) {
                return prev;
              }
              return {
                ...prev,
                [currentKey]: {
                  ...session,
                  streamingState: {
                    ...session.streamingState,
                    lastChunkAt: Date.now(),
                  },
                },
              };
            });
          },
          onMetadata: handleMetadata,
          onCapacity: (chunk: CapacityChunk) => {
            const chatId =
              effectiveChatId ??
              store.get(chatStateAtom)?.[activeStateKeyRef.current]?.metadata
                ?.clientChatId ??
              null;
            if (chatId) {
              store.set(chatCapacityStatusAtomFamily(chatId), chunk.status);
            }
          },
          onError: (err: Error, context?: StreamErrorContext) => {
            logger.error('Chat timeline stream error', {
              error: err,
              sessionId: analyticsSessionId,
              messageCount: messages.length,
            });

            const errorMessage = err.message;
            let isStale = false;

            // For failures with no retained backend trace
            // (backend flushed `outcome:success` then the stream dropped, or the
            // idle timer fired), the terminal RUM report is the only signal.
            // `lifecycle` = where/when it died; classification = what killed it.
            const rumErrorType: EvidenceChatErrorType =
              context?.lifecycle.closeReason === 'idle_timeout'
                ? 'idle_no_activity'
                : 'stream_mid';
            // firstTokenTimeRef latches on the first content chunk, so this is
            // the true "did any content reach the user" signal (streamStartedAt
            // is set at dispatch, before the response opens).
            const contentStarted = firstTokenTimeRef.current !== undefined;
            const errorContextMetadata = context
              ? {
                  ...lifecycleToMetadata(context.lifecycle),
                  error_code: context.errorCode,
                  content_started: contentStarted,
                  ...(context.errorName !== undefined && {
                    error_name: context.errorName,
                  }),
                  ...(context.httpStatus !== undefined && {
                    http_status: context.httpStatus,
                  }),
                  ...(context.errorDetail !== undefined && {
                    error_detail: context.errorDetail,
                  }),
                }
              : { content_started: contentStarted };

            // Resolve the chatId for recovery: prefer the metadata-assigned id
            const recoveryChatId =
              effectiveChatId ??
              store.get(chatStateAtom)?.[activeStateKeyRef.current]?.metadata
                ?.clientChatId ??
              null;

            // If recovery is available, clear streaming state and start polling
            // instead of showing the error to the user. Skip recovery for rate
            // limit so we show RateLimitErrorBlock and toast instead of "processing".
            // Also apply the stale-stream guard: if a newer stream owns this
            // key, skip recovery to avoid interfering with the active stream.
            // Gate solely on the fail-fast guard (flag + offline). Keying on
            // `errorMessage === QUERY_NETWORK` too would skip the 45s recovery
            // wait even with the kill switch off, since an online
            // `Failed to fetch` is also classified as QUERY_NETWORK elsewhere.
            const skipRecoveryForOffline = shouldEvidenceFailFastWhenOffline();

            if (
              recoveryChatId &&
              onStartRecoveryRef.current &&
              !skipRecoveryForOffline &&
              errorMessage !== TIMELINE_STREAM_ERROR.RATE_LIMIT_EXCEEDED &&
              errorMessage !== TIMELINE_STREAM_ERROR.PROMPT_INJECTION &&
              errorMessage !== TIMELINE_STREAM_ERROR.USER_REF_BANNED
            ) {
              let shouldStartRecovery = false;
              // message_info (backendIndex -1) arrives before content, so a
              // failure that triggers recovery usually still has the backend
              // message_id — the join key into the retained completion trace.
              let recoveryMessageId: string | null = null;

              setChatState((prev) => {
                const currentKey = activeStateKeyRef.current;
                const session = prev[currentKey];
                if (
                  !session ||
                  session.streamingState?.abortControllerId !== abortControllerId
                ) {
                  return prev;
                }

                shouldStartRecovery = true;
                recoveryMessageId = session.streamingState?.messageId ?? null;
                return {
                  ...prev,
                  [currentKey]: { ...session, streamingState: null },
                };
              });

              if (!shouldStartRecovery) return;

              // Carry the same classification the error path emits so a
              // recovered turn is still root-causable: error_code / error_detail
              // / http_status / lifecycle split the opaque humanized copy, and
              // message_id joins to the backend completion trace.
              beginStreamRecovery({
                recoveryChatId,
                fallbackMessage: errorMessage,
                terminalMetadata: {
                  result: 'recovery_started',
                  chat_id: recoveryChatId,
                  message_id: recoveryMessageId,
                  error_message: errorMessage,
                  routed_mode: routedModeRef.current,
                  error_type: rumErrorType,
                  ...errorContextMetadata,
                },
              });
              return;
            }

            // No recovery available — use the stale-stream guarded error path.
            // Single setChatState call: all side effects only run when this
            // stream still owns the key.
            if (
              errorMessage === TIMELINE_STREAM_ERROR.RATE_LIMIT_EXCEEDED ||
              context?.errorCode === 'rate_limited' ||
              context?.errorCode === 'concurrency_limited'
            ) {
              const until = computeCooldownUntil(context?.retryAfterSeconds);
              store.set(chatCooldownUntilAtomFamily(activeStateKeyRef.current), until);
              const duration = formatCooldownClock(Math.max(0, until - Date.now()));
              toast({
                title: intl.formatMessage({
                  id: 'chat.rateLimitError.toastTitle',
                  defaultMessage: 'Your rate limit has been exceeded',
                  description: 'Title of the Evidence rate-limit toast',
                }),
                description: intl.formatMessage(
                  {
                    id: 'chat.rateLimitError.toastDescription',
                    defaultMessage: 'Try again in {duration}.',
                    description:
                      'Evidence rate-limit toast. {duration} is a m:ss wait.',
                  },
                  { duration }
                ),
                tone: 'destructive',
              });
            }

            let streamErrorInfo: StreamErrorInfo | null = null;
            let resolvedMessageId: string | null = null;

            setChatState((prev) => {
              const currentKey = activeStateKeyRef.current;
              const session = prev[currentKey];

              if (session?.streamingState?.abortControllerId !== abortControllerId) {
                isStale = true;
                return prev;
              }

              // message_info (backendIndex -1) arrives before content, so a
              // mid-stream failure almost always has the backend message_id.
              resolvedMessageId = session?.streamingState?.messageId ?? null;

              const streamStartedAt = session?.streamingState?.streamStartedAt;
              const durationMs = streamStartedAt ? Date.now() - streamStartedAt : undefined;

              streamErrorInfo = {
                errorMessage,
                durationMs,
                routedMode: routedModeRef.current,
                errorType: 'stream_mid',
                ...(context?.httpStatus !== undefined && {
                  httpStatus: context.httpStatus,
                }),
                ...(context?.errorCode !== undefined && {
                  errorCode: context.errorCode,
                }),
                contentStarted,
              };

              const timelineWithoutPlaceholder = finalizeTimeline(session.timeline);
              const errorEntry: ErrorItemEntry = {
                type: 'error_item',
                id: generateId(),
                content: errorMessage,
                position: timelineWithoutPlaceholder.length,
              };

              return {
                ...prev,
                [currentKey]: {
                  ...session,
                  timeline: [...timelineWithoutPlaceholder, errorEntry] as typeof session.timeline,
                  streamingState: null,
                },
              };
            });

            if (!isStale) {
              setAbortController(null);
              if (effectiveChatId) {
                setStreamingChatIds((prev) => {
                  const next = new Set(prev);
                  next.delete(effectiveChatId);
                  return next;
                });
              }

              finishTracking({
                key: TrackingKey.EVIDENCE_MESSAGE_SEND,
                metadata: {
                  result: 'error',
                  chat_id: effectiveChatId ?? null,
                  message_id: resolvedMessageId,
                  error_message: errorMessage,
                  routed_mode: routedModeRef.current,
                  error_type: rumErrorType,
                  ...errorContextMetadata,
                },
              });
              finishTracking({
                key: TrackingKey.EVIDENCE_IN_SESSION_SUGGESTION_CLICK,
                metadata: {
                  result: 'error',
                  chat_id: effectiveChatId ?? null,
                  message_id: resolvedMessageId,
                  error_message: errorMessage,
                  routed_mode: routedModeRef.current,
                  error_type: rumErrorType,
                  ...errorContextMetadata,
                },
              });

              reportEvidenceStreamRumError({
                errorType: rumErrorType,
                chatId: effectiveChatId ?? null,
                messageId: resolvedMessageId,
                contentStarted,
                routedMode: routedModeRef.current,
                ...(context?.httpStatus !== undefined && {
                  httpStatus: context.httpStatus,
                }),
                ...(context?.errorCode !== undefined && {
                  errorCode: context.errorCode,
                }),
              });

              if (streamErrorInfo) {
                onEvidenceStreamErrorRef.current?.(streamErrorInfo);
              }
              onError?.(errorMessage);
            }
          },
          onComplete: (closeInfo: StreamCloseInfo) => {
            // Calculate references count from timeline before clearing streaming state
            const completionState: {
              streamCompleteInfo?: StreamCompleteInfo;
              tracking?: {
                chatId: string | null;
                referencesCount: number;
                responseLength: number;
                toolsUsed: string[];
                hasRenderedAnswer: boolean;
              };
            } = {};

            setChatState((prev) => {
              const currentKey = activeStateKeyRef.current;
              const session = prev[currentKey];
              if (!session) return prev;

              // Stale-stream guard: a newer stream owns this key — skip all side effects.
              // This prevents a background stream's completion from nullifying the
              // streamingState of a newer active stream on the same key.
              if (session.streamingState?.abortControllerId !== abortControllerId) {
                return prev;
              }

              const timelineWithoutPlaceholder = finalizeTimeline(session.timeline);

              // Scope metrics to only the latest response (after basePosition)
              const latestResponseEntries = getLatestResponseEntries(
                timelineWithoutPlaceholder,
                session.streamingState?.basePosition
              );

              // Count references from web search items in latest response only
              const referencesCount = countReferencesInTimeline(latestResponseEntries);

              // Calculate response length from text items in latest response only
              const responseLength = calculateResponseLength(latestResponseEntries);

              // Extract tools used in latest response only
              const toolsUsed = extractToolsUsed(latestResponseEntries);

              // Calculate word count from text items in latest response only
              const responseWordCount = calculateWordCount(latestResponseEntries);

              // Calculate duration and time to first token
              const streamStartedAt = session.streamingState?.streamStartedAt;
              const durationMs = streamStartedAt ? Date.now() - streamStartedAt : 0;
              const timeToFirstTokenMs =
                streamStartedAt && firstTokenTimeRef.current
                  ? firstTokenTimeRef.current - streamStartedAt
                  : undefined;

              const quotaExhaustedMessageShown =
                hasQuotaExhaustedMessageInTimeline(latestResponseEntries);

              completionState.streamCompleteInfo = {
                durationMs,
                timeToFirstTokenMs,
                referencesCount,
                responseLength,
                tavilySearchCount: tavilySearchCountRef.current,
                routedMode: routedModeRef.current,
                toolsUsed: toolsUsed.length > 0 ? toolsUsed : undefined,
                responseWordCount: responseWordCount > 0 ? responseWordCount : undefined,
                messageId: session.streamingState?.messageId,
                chatId: session.metadata?.clientChatId ?? undefined,
                ...(quotaExhaustedMessageShown && {
                  quotaExhaustedMessageShown: true,
                }),
              };

              completionState.tracking = {
                chatId: session.metadata?.clientChatId ?? effectiveChatId ?? null,
                referencesCount,
                responseLength,
                toolsUsed,
                hasRenderedAnswer: latestResponseEntries.some(isVisibleAnswerEntry),
              };

              // DAT-605 FIX: Bump lastActivity to NOW when streaming completes.
              // Previously, lastActivity stayed at the stream-start timestamp,
              // which was always older than backendUpdatedAt (set at save time).
              // This caused buildLegacyTimeline's freshness guard to pick
              // "backend" over "local" on every post-stream refetch, potentially
              // overwriting the local timeline with stale/incomplete backend data.
              return {
                ...prev,
                [currentKey]: {
                  ...session,
                  timeline: timelineWithoutPlaceholder,
                  streamingState: null,
                  metadata: {
                    ...session.metadata,
                    lastActivity: Date.now(),
                  },
                },
              };
            });

            if (!completionState.streamCompleteInfo || !completionState.tracking) return;
            const { streamCompleteInfo, tracking: completionTracking } = completionState;

            // EVI-2117: the stream closed cleanly, but a backend that advertised
            // terminal-sentinel support and never sent it was cut off mid-answer.
            // Score that as an error so a truncated turn stops being reported as
            // success; genuine completions (and pre-sentinel backends) stay
            // 'success'. When content did arrive, this is telemetry-only — the
            // timeline still renders it; the content-less case recovers below.
            const outcome:
              | { result: 'error'; error_type: EvidenceChatErrorType }
              | { result: 'success' } = closeInfo?.truncated
              ? {
                  result: 'error',
                  error_type: 'clean_close_truncated',
                }
              : { result: 'success' };

            // A truncated close that rendered no answer is a silent failure:
            // the user's message would sit with no reply and no error, and the
            // backend has usually finished the turn after the stream was cut.
            // Route it through the same recovery polling as a mid-stream drop
            // (result: recovery_started, no RUM error). Truncations that
            // already rendered a visible answer stay telemetry-only: replacing
            // it with a reconnecting state would be worse than keeping it.
            if (outcome.result === 'error' && !completionTracking.hasRenderedAnswer) {
              const recoveryChatId = completionTracking.chatId;
              if (recoveryChatId && onStartRecoveryRef.current) {
                beginStreamRecovery({
                  recoveryChatId,
                  fallbackMessage: TIMELINE_STREAM_ERROR.QUERY_FAILED,
                  terminalMetadata: {
                    result: 'recovery_started',
                    chat_id: recoveryChatId,
                    message_id: streamCompleteInfo.messageId ?? null,
                    routed_mode: routedModeRef.current,
                    error_type: outcome.error_type,
                    ...lifecycleToMetadata(closeInfo.lifecycle),
                    content_started: firstTokenTimeRef.current !== undefined,
                  },
                });
                return;
              }

              // No chat to poll — surface the failure in the timeline instead
              // of leaving the sent message with no reply at all.
              addErrorToTimeline(TIMELINE_STREAM_ERROR.QUERY_FAILED);
              onError?.(TIMELINE_STREAM_ERROR.QUERY_FAILED);
            }

            if (outcome.result === 'error') {
              reportEvidenceStreamRumError({
                errorType: outcome.error_type,
                chatId: completionTracking.chatId,
                messageId: streamCompleteInfo.messageId ?? null,
                contentStarted: firstTokenTimeRef.current !== undefined,
                routedMode: routedModeRef.current,
              });
            }
            // Deferring the RUM call opens a window in which the user could
            // submit again and startTracking would replace these entries.
            // Snapshot which run we are finishing so the fresh one can never be
            // closed out with this turn's data.
            const trackedKeys = [
              TrackingKey.EVIDENCE_MESSAGE_SEND,
              TrackingKey.EVIDENCE_IN_SESSION_SUGGESTION_CLICK,
            ] as const;
            const trackingSnapshots = new Map<
              TrackingKey,
              { instanceId: number; startTime: number }
            >();
            trackedKeys.forEach((key) => {
              const entry = getActiveTrackings().get(key);
              if (entry) {
                trackingSnapshots.set(key, {
                  instanceId: entry.instanceId,
                  startTime: entry.startTime,
                });
              }
            });

            setAbortController(null);
            onEvidenceStreamCompleteRef.current?.(streamCompleteInfo);
            appendCheckpoint({
              key: TrackingKey.EVIDENCE_MESSAGE_SEND,
              checkpointName: 'response_completed',
              metadata: {
                chat_id: completionTracking.chatId,
              },
            });
            appendCheckpoint({
              key: TrackingKey.EVIDENCE_IN_SESSION_SUGGESTION_CLICK,
              checkpointName: 'response_completed',
              metadata: {
                chat_id: completionTracking.chatId,
              },
            });
            // The RUM call is deferred until resource timing lands, so stamp the
            // completion time here rather than letting finishTracking read the
            // clock after the wait. Must be taken after the checkpoints above:
            // they stamp their own `performance.now()`, so an earlier reading
            // would make the trailing `response_completed -> end` phase negative.
            const completedAt = performance.now();
            const emitTracking = (timing: NetworkTimingCaptureResult) => {
              const timingMetadataFor = (key: TrackingKey) => {
                const snapshot = trackingSnapshots.get(key);
                const networkDurationMs =
                  snapshot && timing.responseEnd !== undefined
                    ? timing.responseEnd - snapshot.startTime
                    : undefined;
                const mainThreadDurationMs =
                  snapshot && closeInfo.lastEventTime !== undefined
                    ? closeInfo.lastEventTime - snapshot.startTime
                    : undefined;

                return {
                  network_timing_status: timing.status,
                  ...(networkDurationMs !== undefined &&
                    networkDurationMs >= 0 && {
                      network_stream_duration_ms: networkDurationMs,
                    }),
                  ...(mainThreadDurationMs !== undefined &&
                    mainThreadDurationMs >= 0 && {
                      main_thread_stream_duration_ms: mainThreadDurationMs,
                    }),
                };
              };
              // Blocks only genuine mis-attribution: a run that is present but is
              // not the one this turn started. An already-absent entry is left to
              // finishTracking, which no-ops on it as it always has.
              const mayFinishTracking = (key: TrackingKey) => {
                const current = getActiveTrackings().get(key);
                return (
                  current === undefined ||
                  current.instanceId === trackingSnapshots.get(key)?.instanceId
                );
              };

              const lifecycleMetadata = {
                ...lifecycleToMetadata(closeInfo.lifecycle),
                content_started: firstTokenTimeRef.current !== undefined,
              };
              if (mayFinishTracking(TrackingKey.EVIDENCE_MESSAGE_SEND)) {
                finishTracking({
                  key: TrackingKey.EVIDENCE_MESSAGE_SEND,
                  endTime: completedAt,
                  metadata: {
                    ...outcome,
                    chat_id: completionTracking.chatId,
                    message_id: streamCompleteInfo.messageId ?? null,
                    references_count: completionTracking.referencesCount,
                    response_length: completionTracking.responseLength,
                    routed_mode: routedModeRef.current,
                    ...timingMetadataFor(TrackingKey.EVIDENCE_MESSAGE_SEND),
                    ...lifecycleMetadata,
                    tools_used:
                      completionTracking.toolsUsed.length > 0
                        ? completionTracking.toolsUsed
                        : undefined,
                  },
                });
              }
              if (mayFinishTracking(TrackingKey.EVIDENCE_IN_SESSION_SUGGESTION_CLICK)) {
                finishTracking({
                  key: TrackingKey.EVIDENCE_IN_SESSION_SUGGESTION_CLICK,
                  endTime: completedAt,
                  metadata: {
                    ...outcome,
                    chat_id: completionTracking.chatId,
                    message_id: streamCompleteInfo.messageId ?? null,
                    references_count: completionTracking.referencesCount,
                    response_length: completionTracking.responseLength,
                    routed_mode: routedModeRef.current,
                    ...timingMetadataFor(
                      TrackingKey.EVIDENCE_IN_SESSION_SUGGESTION_CLICK
                    ),
                    ...lifecycleMetadata,
                  },
                });
              }
            };

            void closeInfo.networkTiming.then(emitTracking);

            if (effectiveChatId) {
              setStreamingChatIds((prev) => {
                const next = new Set(prev);
                next.delete(effectiveChatId);
                return next;
              });
            }

            onStreamCompleteRef.current?.();

            // Invalidate chat-by-id so cache refetches with new messages from server
            if (effectiveChatId) {
              void queryClient.invalidateQueries({
                queryKey: [GET_CHAT_BY_ID, effectiveChatId],
              });
            }

            // Invalidate session chats to refresh the dropdown list
            if (sessionId) {
              void queryClient.invalidateQueries({
                queryKey: [GET_SESSION_CHATS, sessionId],
              });
            }

            // Refresh session documents when a form fill completed
            if (sessionId && completionTracking.toolsUsed.includes('fill_pdf_form')) {
              void queryClient.invalidateQueries({
                queryKey: [GET_DOCUMENTS_BY_ID, { sessionId }],
              });
            }

            // Invalidate user query to refresh quota values and trigger toast
            void queryClient.invalidateQueries({
              queryKey: ['getUser'],
            });
          },
          onStreamSettled: (lifecycle: StreamLifecycle) => {
            // Terminal-coverage backstop. The SSE promise settled
            // without a terminal reaching onComplete/onError — the library
            // silently resolved on an abort, or resolved without ever calling
            // onclose. Before this, such a send had NO terminal RUM report
            // until the 5-min global watchdog fired (minutes late, minimal
            // metadata), and a genuine silent resolve was never reported at
            // all. Emit the terminal here so every send is visible in RUM
            // within seconds, classified by close_reason + lifecycle counters.
            //
            // RUM-only: we deliberately do NOT clear streamingState or touch
            // the timeline. The global watchdog still owns the user-visible
            // "stuck" error + spinner clear; this is pure observability.
            //
            // Read the live session through a non-mutating setChatState updater
            // (returns prev untouched) — the same way onComplete/onError reach
            // current state — so the stale guard sees fresh state without a
            // separate store subscription.
            //
            // Stale guard: a newer stream, or the watchdog, already took over
            // this key. Bail so we never close out a different turn — and
            // finishTracking is exactly-once, so a later watchdog/completion is
            // a harmless no-op when we do fire.
            let ownsSettledKey = false;
            let settledClientChatId: string | undefined;
            let settledMessageId: string | undefined;
            setChatState((prev) => {
              const session = prev[activeStateKeyRef.current];
              ownsSettledKey =
                session?.streamingState?.abortControllerId === abortControllerId;
              settledClientChatId = session?.metadata?.clientChatId;
              settledMessageId = session?.streamingState?.messageId;
              return prev;
            });

            if (!ownsSettledKey) {
              return;
            }

            const settledMetadata = {
              result: 'error' as const,
              // Separate error_types so the benign user/programmatic abort can
              // be filtered out of error-rate dashboards, leaving the
              // pathological silent stall (the incident) on its own.
              error_type:
                lifecycle.closeReason === 'abort'
                  ? ('client_abort' as const)
                  : ('silent_stream_stall' as const),
              chat_id: settledClientChatId ?? effectiveChatId ?? null,
              message_id: settledMessageId ?? null,
              routed_mode: routedModeRef.current,
              content_started: firstTokenTimeRef.current !== undefined,
              ...lifecycleToMetadata(lifecycle),
            };

            finishTracking({
              key: TrackingKey.EVIDENCE_MESSAGE_SEND,
              metadata: settledMetadata,
            });
            finishTracking({
              key: TrackingKey.EVIDENCE_IN_SESSION_SUGGESTION_CLICK,
              metadata: settledMetadata,
            });

            // A `client_abort` is a benign user/programmatic cancel, not a
            // failure — exclude it so Error Tracking and the error rate stay
            // clean. Only the pathological silent stall is reported.
            if (settledMetadata.error_type !== 'client_abort') {
              reportEvidenceStreamRumError({
                errorType: settledMetadata.error_type,
                chatId: settledMetadata.chat_id,
                messageId: settledMetadata.message_id,
                contentStarted: settledMetadata.content_started,
                routedMode: routedModeRef.current,
              });
            }
          },
          abortController: newAbortController,
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Streaming failed';
        // This catch fires before the stream opens, so no content ever reached
        // the user. Classify here too so a stream_start failure carries the same
        // PHI-safe cause fields as a mid-stream one.
        const { httpStatus, errorCode } = classifyStreamError(err, 'onerror');
        logger.error('Failed to start chat timeline stream', {
          error: err,
          sessionId: analyticsSessionId,
          messageCount: messages.length,
        });
        setAbortController(null);

        if (effectiveChatId) {
          setStreamingChatIds((prev) => {
            const next = new Set(prev);
            next.delete(effectiveChatId);
            return next;
          });
        }

        onEvidenceStreamErrorRef.current?.({
          errorMessage,
          errorType: 'stream_start',
          ...(httpStatus !== undefined && { httpStatus }),
          errorCode,
          contentStarted: false,
        });
        finishTracking({
          key: TrackingKey.EVIDENCE_MESSAGE_SEND,
          metadata: {
            result: 'error',
            chat_id: effectiveChatId ?? null,
            // Stream never opened, so the message_info chunk (which carries the
            // backend message_id) never arrived — it is unknown here by design.
            message_id: null,
            error_message: errorMessage,
            error_type: 'stream_start',
            ...(httpStatus !== undefined && { http_status: httpStatus }),
            error_code: errorCode,
            content_started: false,
          },
        });
        finishTracking({
          key: TrackingKey.EVIDENCE_IN_SESSION_SUGGESTION_CLICK,
          metadata: {
            result: 'error',
            chat_id: effectiveChatId ?? null,
            message_id: null,
            error_message: errorMessage,
            error_type: 'stream_start',
            ...(httpStatus !== undefined && { http_status: httpStatus }),
            error_code: errorCode,
            content_started: false,
          },
        });

        reportEvidenceStreamRumError({
          errorType: 'stream_start',
          chatId: effectiveChatId ?? null,
          messageId: null,
          contentStarted: false,
          routedMode: routedModeRef.current,
          ...(httpStatus !== undefined && { httpStatus }),
          ...(errorCode !== undefined && { errorCode }),
        });

        addErrorToTimeline(errorMessage);
        onError?.(errorMessage);
      }
    },
    [
      sessionId,
      chatId,
      analyticsSessionId,
      setChatState,
      processChunk,
      handleMetadata,
      addErrorToTimeline,
      mode,
      version,
      content,
      domains,
      includeUserDocs,
      includeWebSearch,
      evidenceSourceFilters,
      sourceListIds,
      userDocCollectionIds,
      collectionIds,
      integrationPartners,
      setAbortController,
      setStreamingChatIds,
      store,
    ]
  );

  // SWR-171: when the browser fires `offline` mid-stream, abort and show the
  // network notice instead of waiting for fetch timeout / recovery. This does
  // not gate the send — `navigator.onLine` alone is not proof the tab cannot
  // reach the API (EVI-2557). A real transport failure is classified after
  // the request.
  useEffect(() => {
    if (!IS_EVIDENCE_OFFLINE_FAIL_FAST_ENABLED()) return;

    const handleOffline = () => {
      const currentKey = activeStateKeyRef.current;
      const session = store.get(chatStateAtom)?.[currentKey];
      if (!session?.streamingState) return;

      const abortController = store.get(chatAbortControllersAtom(stateKeyRef.current));
      if (abortController) {
        abortStreamWithReason(abortController, STREAM_ABORT_REASON.PROGRAMMATIC);
      }
      setAbortController(null);
      addErrorToTimeline(TIMELINE_STREAM_ERROR.QUERY_NETWORK);
    };

    window.addEventListener('offline', handleOffline);
    return () => window.removeEventListener('offline', handleOffline);
  }, [addErrorToTimeline, setAbortController, store]);

  // On unmount, detach the abort controller from the atom WITHOUT aborting it.
  // The SSE stream continues running in the background — its callbacks use
  // global Jotai setters and TanStack Query, so state updates still land.
  // Detaching prevents a future mount's startStreaming from finding a stale
  // controller and aborting a still-running stream (e.g., sidebar close/reopen).
  useEffect(() => {
    return () => {
      setAbortController(null);
    };
  }, [setAbortController]);

  return {
    startStreaming,
    streamingModeRef,
    routedModeRef,
  };
};
